// Pure mapping and injected RPC transport: no credentials or browser storage.
const invalid = () => {
  throw new Error("학교·학년과 날짜별 시험·직보 입력을 확인해 주세요.");
};
export function examDateRows(start, end) {
  const parse = (key) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key || "") || key.startsWith("0000")) {
      return invalid();
    }
    const date = new Date(`${key}T00:00:00Z`);
    if (
      !Number.isFinite(date.getTime()) ||
      date.toISOString().slice(0, 10) !== key
    ) return invalid();
    return date.getTime();
  };
  const first = parse(start), last = parse(end);
  if (last < first) return invalid();
  const rows = [];
  for (let day = first; day <= last; day += 86400000) {
    rows.push(new Date(day).toISOString().slice(0, 10));
  }
  return rows;
}
export function mapExamSnapshot(snapshot) {
  if (!snapshot || !Array.isArray(snapshot.folders)) {
    throw new Error("시험 일정을 불러오지 못했습니다. 다시 조회해 주세요.");
  }
  return snapshot.folders.map((folder) => ({
    id: folder.id,
    title: folder.name.replace(/^\d{4} · /, ""),
    year: folder.name.match(/^\d{4}/)?.[0] || "",
    periods: folder.periods.map((period) => {
      const subjects = Object.fromEntries(
        examDateRows(period.inputStart, period.inputEnd)
          .map((date) => [date, { subject: "", direct: "" }]),
      );
      for (const entry of period.subjectEntries) {
        const plan = subjects[entry.date];
        if (!plan) return invalid();
        plan.subject = [plan.subject, entry.name].filter(Boolean).join(", ");
      }
      for (const entry of period.directEntries) {
        if (!subjects[entry.date]) return invalid();
        subjects[entry.date].direct = entry.note;
      }
      return {
        ...period,
        grade: period.grade == null ? "" : String(period.grade),
        subjects,
      };
    }),
  }));
}
export function examSchoolOptions(schools = []) {
  const selected = schools.filter((s) =>
    s.choiceId && s.active !== false && s.isActive !== false
  ).map((s) => ({
    value: s.choiceId,
    label: s.name,
    detail: [
      s.name,
      s.schoolLevel,
      [s.city, s.district].filter(Boolean).join(" "),
    ].filter(
      Boolean,
    ).join(" · "),
    schoolLevel: s.schoolLevel,
    name: s.name,
  }));
  return selected.map((s) => ({
    ...s,
    detail: selected.filter((other) => other.detail === s.detail).length > 1
      ? `${s.detail} · ${s.value}`
      : s.detail,
  }));
}
export function prepareExamSave(
  folderId,
  period,
  requestId = crypto.randomUUID(),
) {
  if (!period.schoolChoiceId || !period.grade) return invalid();
  const dates = examDateRows(
    period.inputStart ?? period.start,
    period.inputEnd ?? period.end,
  );
  const days = dates.map((date) => {
    const plan = period.subjects?.[date] || {};
    const subject = typeof plan === "string" ? plan : plan.subject || "";
    const original = (period.subjectEntries || []).filter((entry) =>
      entry.date === date
    ).map((entry) => entry.name);
    // Preserve old subject boundaries if its displayed text has not been edited.
    const names = original.length && original.join(", ") === subject
      ? original
      : subject.split(",").map((s) => s.trim()).filter(Boolean);
    return {
      date,
      subjects: names,
      directNote: typeof plan === "string" ? null : plan.direct?.trim() || null,
    };
  });
  return {
    requestId,
    payload: {
      folderId,
      periodId: period.revision ? period.id : null,
      schoolChoiceId: period.schoolChoiceId,
      grade: Number(period.grade),
      expectedRevision: period.revision || null,
      days,
    },
  };
}
const messages = {
  "40001":
    "다른 직원이 일정을 수정했습니다. 다시 조회한 뒤 변경 내용을 확인해 주세요.",
  "23505": "저장 요청이 기존 요청과 다릅니다. 저장 결과를 확인해 주세요.",
  "22023": "학교·학년과 날짜별 시험·직보 입력을 확인해 주세요.",
  "42501": "시험 일정을 저장할 권한이 없습니다.",
  "P0001": "시험 일정을 저장하지 못했습니다. 입력 내용은 유지됩니다.",
};
export function createExamRepository(rpc) {
  const result = (requestId) =>
    rpc("get_exam_schedule_save_result", { save_request_id: requestId });
  return {
    load: async (folderId = null) =>
      mapExamSnapshot(
        await rpc("list_exam_schedule_v3", { input_folder_id: folderId }),
      ),
    result,
    async save(request) {
      try {
        return await rpc("save_exam_schedule_v3", {
          save_request_id: request.requestId,
          schedule_input: request.payload,
        });
      } catch (failure) {
        if (messages[failure.code]) {
          const error = new Error(messages[failure.code]);
          error.code = failure.code;
          throw error;
        }
        let saved;
        try {
          saved = await result(request.requestId);
        } catch { /* Keep the original request for another result check. */ }
        if (saved?.status === "saved") return { ...saved, recovered: true };
        const error = new Error(
          "저장 결과를 확인하지 못했습니다. 입력을 유지하고 같은 요청으로 다시 확인해 주세요.",
        );
        error.code = "EXAM_SAVE_UNCONFIRMED";
        error.requestId = request.requestId;
        throw error;
      }
    },
    removePeriod: (periodId) =>
      rpc("soft_delete_exam_period", { target_period_id: periodId }),
    removeFolder: (folderId) =>
      rpc("soft_delete_exam_folder", { target_folder_id: folderId }),
  };
}
